/* Live map · map first, the sidebar collapses to a rail. Design: ConsoleLiveMap.dc.html.
   The map fills the whole body; the bus list floats on the left and the
   selected bus's card on the right. The model has no stop list, so the card
   shows only what is real: status, aboard, driver, speed and the last fix. */
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFleet } from '../data/FleetData';
import type { Bus } from '../data/model';
import { usePage, useShell } from '../Shell';
import MapView from '../MapView';
import { announceToBusParents } from '../../services/fleetContactApi';
import { Empty, IcArrow, IcX, StaleBanner, StatusChip, PageBody, ago, hhmm, plateBeside, plural, useAction, useGo } from '../kit';

type Filter = 'all' | 'route' | 'look';

/* Cancel the body padding so the map is edge to edge (the shell pads the body
   28/32/48, or 20/16/40 at 820 px and below). */
const LOCAL_CSS = `
.tc .lm-root{position:relative;margin:-28px -32px -48px;height:calc(100% + 76px);min-height:560px;overflow:hidden}
.tc .lm-list{position:absolute;left:20px;top:20px;bottom:20px;width:340px;display:flex;flex-direction:column;box-shadow:0 12px 32px -16px rgba(17,21,32,.3);z-index:2}
.tc .lm-card{position:absolute;right:20px;bottom:20px;width:380px;max-height:calc(100% - 40px);overflow:auto;box-shadow:0 20px 44px -18px rgba(17,21,32,.35);z-index:3}
.tc .lm-stale{position:absolute;left:380px;right:20px;top:20px;z-index:2}
.tc .lm-root textarea.tin{height:auto;padding:10px 12px;resize:vertical;line-height:20px}
@container tc (max-width: 820px){
.tc .lm-root{margin:-20px -16px -40px;height:calc(100% + 60px)}
.tc .lm-list{left:12px;right:12px;top:12px;bottom:auto;width:auto;max-height:42%}
.tc .lm-card{left:12px;right:12px;bottom:12px;width:auto;max-height:52%}
.tc .lm-stale{left:12px;right:12px;top:auto;bottom:12px}
}`;

const Skeleton: React.FC = () => (
  <div className="lm-root">
    <style>{LOCAL_CSS}</style>
    <div className="shim" style={{ width: '100%', height: '100%', borderRadius: '0', position: 'absolute', inset: '0' }} />
    <div className="panel" style={{ position: 'absolute', left: '20px', top: '20px', width: '320px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div className="shim" style={{ width: '60%', height: '18px' }} />
      <div className="shim" style={{ width: '100%', height: '36px' }} />
      {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="shim" style={{ width: '100%', height: '44px' }} />)}
    </div>
  </div>
);

const isOnRoute = (b: Bus) => b.status === 'on_route' || b.status === 'late';
const needsLook = (b: Bus) => !!b.needsLook || b.status === 'late' || b.status === 'off_road';

/** The row's second line: only facts the record holds. */
function rowLine(b: Bus): string {
  if (b.needsLook) return b.needsLook;
  const bits: string[] = [];
  if (b.aboard != null) bits.push(`${b.aboard} aboard`);
  if (b.lastSeen) bits.push(`Seen ${ago(b.lastSeen)}`);
  else if (b.lat == null) bits.push('No location yet');
  return bits.join(' · ') || (b.driverName ? b.driverName : 'No driver');
}

const LiveMap: React.FC = () => {
  const f = useFleet();
  const { dark, openAddBus } = useShell();
  const go = useGo();
  const action = useAction();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>('all');
  const [telling, setTelling] = useState(false);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);

  const onRoad = f.buses.filter(isOnRoute);
  const look = f.buses.filter(needsLook);
  const late = f.buses.filter((b) => b.status === 'late');
  const worst = late[0] || look[0];

  usePage({
    key: 'ConsoleLiveMap', title: 'Live map', rail: true,
    zanaContext: `Live map · ${plural(onRoad.length, 'bus', 'buses')} on route`,
    zanaIntro: 'I can see every bus on this map.',
    prompts: [
      late[0] ? `Why is ${late[0].name} late?` : 'Which buses are on the road right now?',
      'Which buses have not sent a location today?',
      worst ? `What should I tell ${worst.name} parents?` : 'Which buses need a look today?',
    ],
  });

  const shown = useMemo(() => (filter === 'route' ? f.buses.filter(isOnRoute) : filter === 'look' ? f.buses.filter(needsLook) : f.buses), [f.buses, filter]);
  const selId = params.get('bus');
  const sel = selId ? f.buses.find((b) => b.id === selId) || null : null;

  const select = (id: string | null) => {
    setTelling(false); setMsg('');
    const next = new URLSearchParams(params);
    if (id) next.set('bus', id); else next.delete('bus');
    setParams(next, { replace: true });
  };

  const run = sel ? f.runs.find((r) => r.busId === sel.id && !r.finished && r.date.toDateString() === new Date().toDateString()) : undefined;
  const first = sel?.driverName.split(/\s+/)[0] || '';

  const tellParents = async () => {
    if (!sel || !msg.trim() || sending) return;
    const bus = sel; const text = msg.trim();
    setSending(true);
    const r = await action({
      busy: `Sending to ${bus.name} parents…`,
      done: (res: { sent?: number; total?: number }) => `Sent to ${res.sent ?? 0} of ${plural(res.total ?? 0, 'parent')}`,
      fail: `Couldn’t send to ${bus.name} parents. Try again in a minute.`,
      run: async () => {
        const res = await announceToBusParents(f.org.code, bus.id, text);
        if (!res.ok) throw new Error(res.message || 'not sent');
        return res;
      },
    });
    setSending(false);
    if (r && (r.sent ?? 0) > 0) { setMsg(''); setTelling(false); }
  };

  const segBtn = (k: Filter, label: string, n: number) => (
    <button type="button" role="tab" aria-selected={filter === k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{label} {n}</button>
  );

  return (
    <PageBody loading={f.loading} skeleton={<Skeleton />}>
      <div className="lm-root mapday">
        <style>{LOCAL_CSS}</style>
        <div style={{ position: 'absolute', inset: '0' }}>
          <MapView buses={f.buses} gate={f.org.gate} dark={dark} selectedId={sel?.id || null} onSelect={(id) => select(id)} label="Live map of your buses" />
        </div>

        {f.error && <div className="lm-stale"><StaleBanner what="bus positions" since={f.updatedAt} onRetry={f.retry} /></div>}

        <section className={'panel lm-list' + (f.error ? ' stale' : '')} aria-label="Buses">
          <div style={{ padding: '16px 16px 10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h1 className="h2">Live buses</h1>
              {f.updatedAt && (
                <span className="sm" style={{ color: 'var(--muted)', display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                  <span className="livedot" />{hhmm(f.updatedAt)}
                </span>
              )}
            </div>
            {f.buses.length > 0 && (
              <div className="seg" role="tablist" aria-label="Show">
                {segBtn('all', 'All', f.buses.length)}
                {segBtn('route', 'On route', onRoad.length)}
                {segBtn('look', 'Needs a look', look.length)}
              </div>
            )}
          </div>
          <div style={{ flex: '1', overflow: 'auto', padding: '0 8px 8px' }}>
            {f.buses.length === 0 ? (
              <Empty title="No buses yet" body="Add a bus and its driver. When the driver taps Start in the Trasse app, the bus appears on this map.">
                <button type="button" className="btn s b-ink" onClick={openAddBus}>Add a bus</button>
              </Empty>
            ) : shown.length === 0 ? (
              <p className="sm" style={{ margin: '8px 10px', color: 'var(--muted)' }}>
                {filter === 'route' ? 'No bus is on a run right now. Buses appear here when their driver taps Start.' : 'Nothing needs a look. Late buses and buses with a problem show here.'}
                {' '}<button type="button" className="btn s b-ghost" onClick={() => setFilter('all')}>Show all</button>
              </p>
            ) : shown.map((b) => (
              <button key={b.id} type="button" className={'pal-i' + (b.id === sel?.id ? ' hi' : '')} aria-pressed={b.id === sel?.id}
                onClick={() => select(b.id)} style={{ height: 'auto', padding: '10px', alignItems: 'flex-start' }}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '0' }}>
                  <b style={{ fontSize: '15px' }}>{b.name}{plateBeside(b) ? <> <span className="mono">{b.plate}</span></> : null}</b>
                  <span className="sm" style={{ color: 'var(--muted)' }}>{rowLine(b)}</span>
                </span>
                <span style={{ marginLeft: 'auto' }}><StatusChip status={b.status} label={b.statusLabel} /></span>
              </button>
            ))}
          </div>
        </section>

        {sel && (
          <section className="panel lm-card" aria-label={sel.name}>
            <div style={{ padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, borderBottom: '1px solid var(--line)' }}>
              <div style={{ minWidth: 0 }}>
                <span className="mono">{sel.plate}{sel.driverName ? ` · ${sel.driverName}` : ' · No driver'}</span>
                <h2 className="h2" style={{ marginTop: '2px' }}>{sel.name}{run ? ` · ${run.type}` : ''}</h2>
              </div>
              <span className="row" style={{ gap: 4 }}>
                <StatusChip status={sel.status} label={sel.statusLabel} />
                <button type="button" className="ibtn" aria-label="Close bus card" style={{ width: 32, height: 32 }} onClick={() => select(null)}><IcX /></button>
              </span>
            </div>
            <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', borderBottom: '1px solid var(--line)' }}>
              <div>
                <span className="sm" style={{ color: 'var(--muted)' }}>Aboard</span>
                <div className="big" style={{ fontSize: '26px' }}>
                  {sel.aboard != null ? <>{sel.aboard}{sel.expected != null && <span style={{ fontSize: '16px', color: 'var(--faint)' }}> / {sel.expected}</span>}</> : <span style={{ color: 'var(--faint)' }}>—</span>}
                </div>
              </div>
              <div>
                <span className="sm" style={{ color: 'var(--muted)' }}>Speed</span>
                <div className="big" style={{ fontSize: '26px' }}>
                  {sel.tracking && sel.speedKmh != null ? <>{Math.round(sel.speedKmh)}<span style={{ fontSize: '16px', color: 'var(--faint)' }}> km/h</span></> : <span style={{ color: 'var(--faint)' }}>—</span>}
                </div>
              </div>
              <div>
                <span className="sm" style={{ color: 'var(--muted)' }}>Last update</span>
                <div className="big" style={{ fontSize: '26px' }}>{sel.lastSeen ? hhmm(sel.lastSeen) : <span style={{ color: 'var(--faint)' }}>—</span>}</div>
              </div>
            </div>
            <div style={{ padding: '14px 18px 6px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sel.lastSeen && sel.lastSeen.toDateString() !== new Date().toDateString() && (
                <span className="sm" style={{ color: 'var(--muted)' }}>Last location {ago(sel.lastSeen)}. The bus has not sent one today.</span>
              )}
              {sel.lat == null && <span className="sm" style={{ color: 'var(--muted)' }}>No location yet. It appears when the driver taps Start in the Trasse app.</span>}
              {sel.needsLook && <span className="sm" style={{ color: 'var(--warn)', fontWeight: 600 }}>{sel.needsLook}</span>}
              <a className="btn s b-ghost" style={{ alignSelf: 'flex-start' }} {...go('ConsoleBuses', `?bus=${encodeURIComponent(sel.id)}`)}>Bus details <IcArrow /></a>
            </div>
            {telling ? (
              <div style={{ padding: '8px 18px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="field">
                  <label htmlFor="lm-tell">Message to {sel.name} parents</label>
                  <textarea id="lm-tell" className="tin" rows={3} value={msg} maxLength={500} onChange={(e) => setMsg(e.target.value)}
                    placeholder={`${sel.name} bus is running about 10 minutes late.`} autoFocus />
                </div>
                <span className="sm" style={{ color: 'var(--muted)' }}>Sent in the Trasse app to every parent linked to this bus.</span>
                <div className="row" style={{ gap: 8 }}>
                  <button type="button" className="btn b-ink" style={{ flex: 1 }} disabled={!msg.trim() || sending || !f.org.code} onClick={() => { void tellParents(); }}>Send to parents</button>
                  <button type="button" className="btn b-line" style={{ flex: 1 }} onClick={() => { setTelling(false); setMsg(''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '12px 18px 16px', display: 'flex', gap: '8px' }}>
                {sel.driverPhone && (
                  <a className="btn b-ink" style={{ flex: '1' }} href={`tel:${sel.driverPhone.replace(/\s+/g, '')}`}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2Z" />
                    </svg>
                    Call {first || 'driver'}
                  </a>
                )}
                <button type="button" className={'btn ' + (sel.driverPhone ? 'b-line' : 'b-ink')} style={{ flex: '1' }} onClick={() => setTelling(true)}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                    <path d="M3 11v2a1 1 0 0 0 1 1h3l5 4V6L7 10H4a1 1 0 0 0-1 1z" />
                    <path d="M16 9a4 4 0 0 1 0 6M19 6a8 8 0 0 1 0 12" />
                  </svg>
                  Tell parents
                </button>
              </div>
            )}
          </section>
        )}
      </div>
    </PageBody>
  );
};

export default LiveMap;
